import React, { useState, useEffect, useMemo } from 'react';
import { Users, Search, ChevronUp, ChevronDown, RefreshCw, AlertTriangle, Key, UserX, Mail, Download } from 'lucide-react';
import { downloadCSV } from '../utils/csvExport';
import { supabase } from '../supabase';
import { formatDate } from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

export default function UsersPage({ navigateToCompany }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [showResetModal, setShowResetModal] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Using .range(0, 9999) to ensure we get all records (bypasses default limit)
      const [usersRes, userCompaniesRes, companiesRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*', { count: 'exact' })
          .range(0, 9999)
          .order('created_at', { ascending: false }),
        supabase
          .from('user_companies')
          .select('*')
          .range(0, 9999),
        supabase
          .from('companies')
          .select('id, name')
          .range(0, 9999)
          .order('name')
      ]);

      if (usersRes.error) throw usersRes.error;
      if (userCompaniesRes.error) throw userCompaniesRes.error;
      if (companiesRes.error) throw companiesRes.error;

      const usersData = usersRes.data || [];
      const userCompaniesData = userCompaniesRes.data || [];
      const companiesData = companiesRes.data || [];

      console.log(`Loaded: ${usersData.length} users, ${userCompaniesData.length} user_companies, ${companiesData.length} companies`);

      // Build company lookup
      const companyMap = {};
      companiesData.forEach(c => { companyMap[c.id] = c; });

      // Build user-company relationships
      const userCompanyMap = {};
      userCompaniesData.forEach(uc => {
        if (!userCompanyMap[uc.user_id]) {
          userCompanyMap[uc.user_id] = [];
        }
        userCompanyMap[uc.user_id].push({
          ...uc,
          company: companyMap[uc.company_id] || null
        });
      });

      const enriched = usersData.map(u => {
        const rels = userCompanyMap[u.id] || [];
        const primaryRel = rels[0] || null;
        return {
          ...u,
          company: primaryRel?.company || null,
          companyId: primaryRel?.company_id || null,
          role: primaryRel?.role || 'none',
          allCompanies: rels
        };
      });

      setUsers(enriched);
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sendPasswordReset = async (email) => {
    setActionLoading(email);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://workvanapp.com/reset-password'
      });
      if (error) throw error;
      const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch { return 'unknown'; } })();
      await logAdminAction(adminEmail, 'password_reset', 'user', null, email);
      alert(`Password reset email sent to ${email}`);
    } catch (err) {
      alert(`Failed to send password reset: ${err.message}`);
    } finally {
      setActionLoading(null);
      setShowResetModal(null);
    }
  };

  const filtered = useMemo(() => {
    let result = users.filter(u => {
      const matchesSearch = searchTerm === '' ||
        (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompany = filterCompany === 'all' ||
        (filterCompany === 'none' && !u.companyId) ||
        u.companyId === filterCompany;
      const matchesRole = filterRole === 'all' || u.role === filterRole;
      return matchesSearch && matchesCompany && matchesRole;
    });

    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'full_name':
          aVal = (a.full_name || '').toLowerCase();
          bVal = (b.full_name || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'company':
          aVal = (a.company?.name || '').toLowerCase();
          bVal = (b.company?.name || '').toLowerCase();
          break;
        case 'role':
          aVal = a.role;
          bVal = b.role;
          break;
        case 'created_at':
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [users, searchTerm, filterCompany, filterRole, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} style={{ marginLeft: '0.25rem' }} />
      : <ChevronDown size={14} style={{ marginLeft: '0.25rem' }} />;
  };

  const roleBadge = (role) => {
    const colors = {
      owner: { bg: '#ff6b3520', text: '#ff6b35' },
      admin: { bg: '#9b59b620', text: '#9b59b6' },
      member: { bg: '#3498db20', text: '#3498db' },
      none: { bg: '#88888820', text: '#888' }
    };
    const c = colors[role] || colors.none;
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        background: c.bg,
        color: c.text,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'capitalize'
      }}>
        {role}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading users...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ color: '#e74c3c', marginBottom: '1rem' }}>
          <AlertTriangle size={32} style={{ marginBottom: '0.5rem' }} />
          <div>Failed to load users</div>
        </div>
        <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={loadData}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  const thStyle = {
    textAlign: 'left',
    padding: '0.875rem 1rem',
    color: '#888',
    fontWeight: '500',
    fontSize: '0.85rem',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap'
  };

  return (
    <div style={{ padding: '2rem 3rem', boxSizing: 'border-box', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Users
          </h1>
          <p style={{ color: '#888' }}>
            All users across the Work Van platform ({users.length} total)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => downloadCSV(filtered, [
              { key: 'full_name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'role', label: 'Role' },
              { key: 'company.name', label: 'Company' },
              { key: 'created_at', label: 'Created At', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
            ], 'users-export.csv')}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem'
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            onClick={loadData}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              color: '#e0e0e0'
            }}
          />
        </div>

        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '180px'
          }}
        >
          <option value="all">All Companies</option>
          <option value="none">No Company</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '130px'
          }}
        >
          <option value="all">All Roles</option>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
      </div>

      {/* Count */}
      <div style={{
        marginBottom: '1rem',
        fontSize: '0.85rem',
        color: '#888'
      }}>
        Showing {filtered.length} of {users.length} users
      </div>

      {/* Table */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflowX: 'auto'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
              <th style={thStyle} onClick={() => handleSort('full_name')}>
                Full Name <SortIcon field="full_name" />
              </th>
              <th style={thStyle} onClick={() => handleSort('email')}>
                Email <SortIcon field="email" />
              </th>
              <th style={thStyle} onClick={() => handleSort('company')}>
                Company <SortIcon field="company" />
              </th>
              <th style={thStyle} onClick={() => handleSort('role')}>
                Role <SortIcon field="role" />
              </th>
              <th style={thStyle} onClick={() => handleSort('created_at')}>
                Created <SortIcon field="created_at" />
              </th>
              <th style={{ ...thStyle, cursor: 'default', textAlign: 'center' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
                  No users found
                </td>
              </tr>
            ) : (
              filtered.map(user => (
                <tr
                  key={user.id}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#242424'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                      {user.full_name || 'Unnamed'}
                    </div>
                    <div style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {user.id.substring(0, 8)}...
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>
                    {user.email}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {user.company ? (
                      <span
                        onClick={() => navigateToCompany(user.companyId)}
                        style={{
                          color: '#3498db',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        {user.company.name}
                      </span>
                    ) : (
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>-</span>
                    )}
                    {user.allCompanies?.length > 1 && (
                      <span style={{ color: '#666', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                        +{user.allCompanies.length - 1} more
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {roleBadge(user.role)}
                  </td>
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>
                    {formatDate(user.created_at)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={() => setShowResetModal(user)}
                      disabled={actionLoading === user.email}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.4rem 0.75rem',
                        background: '#2a2a2a',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#3a3a3a';
                        e.currentTarget.style.color = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#2a2a2a';
                        e.currentTarget.style.color = '#888';
                      }}
                    >
                      <Key size={14} />
                      Reset
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Password Reset Confirmation Modal */}
      {showResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            border: '1px solid #2a2a2a'
          }}>
            <h3 style={{ color: '#e0e0e0', marginBottom: '1rem' }}>Send Password Reset?</h3>
            <p style={{ color: '#888', marginBottom: '1.5rem' }}>
              This will send a password reset email to:
              <br />
              <strong style={{ color: '#e0e0e0' }}>{showResetModal.email}</strong>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowResetModal(null)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => sendPasswordReset(showResetModal.email)}
                disabled={actionLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#ff6b35',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {actionLoading ? 'Sending...' : <><Mail size={16} /> Send Reset Email</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
